import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CheckCircle, ArrowRight, Radio } from "lucide-react";

const API = import.meta.env.VITE_API_URL || "";

export default function SignalInboxSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [ready, setReady] = useState(false);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (!sessionId) { setReady(true); return; }
    // Poll for tenant creation (webhook may take a few seconds)
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(`${API}/api/marketing/session-tenant/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.status === "ready") {
            setTenant(data);
            setReady(true);
            clearInterval(poll);
          }
        }
      } catch { /* retry */ }
      if (attempts >= 15) { setReady(true); clearInterval(poll); }
    }, 3000);
    return () => clearInterval(poll);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0c1324]">
      <Header />

      <section className="pt-32 pb-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-blue-500/15 border border-blue-500/25 flex items-center justify-center mx-auto mb-8">
            <CheckCircle size={40} className="text-blue-400" />
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 tracking-tight">
            Welcome to Signal Inbox
          </h1>
          <p className="text-lg text-slate-400 mb-8 max-w-lg mx-auto">
            Your subscription is active. Our AI agent is already scanning for leads in your niche.
            You'll see your first draft approvals within 4 hours.
          </p>

          {!ready && (
            <div className="flex items-center justify-center gap-3 text-slate-500 text-sm mb-8">
              <div className="w-4 h-4 border-2 border-blue-500/50 border-t-blue-500 rounded-full animate-spin" />
              Setting up your account...
            </div>
          )}

          {ready && (
            <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-8 mb-8">
              <h2 className="text-lg font-bold text-white mb-4">What happens next?</h2>
              <div className="space-y-4 text-left">
                {[
                  "Our AI scans Reddit and social platforms every 4 hours",
                  "High-signal posts get personalized expert replies drafted",
                  "You'll receive drafts in your approval queue",
                  "One-click approve — the reply posts automatically",
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/15 border border-blue-500/25 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-blue-400 text-xs font-bold">{i + 1}</span>
                    </div>
                    <p className="text-slate-300 text-sm">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={tenant?.dashboard_url || "/marketing"}
              className="inline-flex items-center gap-2 bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] transition-all"
            >
              <Radio size={18} /> Open My Dashboard <ArrowRight size={18} />
            </Link>
            <Link
              to="/"
              className="text-slate-500 hover:text-slate-300 text-sm font-medium transition-colors"
            >
              Back to Home
            </Link>
          </div>

          {tenant && (
            <div className="mt-6 bg-slate-900/60 border border-slate-800/60 rounded-2xl p-6 text-left max-w-md mx-auto">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-3">Your credentials (also emailed to you)</p>
              <div className="space-y-2">
                <div>
                  <p className="text-[10px] text-slate-500">Tenant ID</p>
                  <p className="text-sm text-blue-400 font-mono">{tenant.tenant_id}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-500">Tier</p>
                  <p className="text-sm text-white font-semibold capitalize">{tenant.tier}</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-600 mt-3">Bookmark your dashboard link — you'll also find it in your welcome email.</p>
            </div>
          )}

          {sessionId && (
            <p className="text-slate-600 text-xs mt-8">
              Session: {sessionId}
            </p>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
